import { IContactIntelligence, ContactEnrichmentStatus } from "../Types/account";

/**
 * Check if contact is successfully enriched
 * Only ENRICHED_SUCCESS contacts count toward subscription limits
 */
export function isSuccessfullyEnriched(contact: IContactIntelligence): boolean {
  return contact.enrichment_status === ContactEnrichmentStatus.ENRICHED_SUCCESS;
}

/**
 * Check if contact has been through enrichment (success or no data)
 * This includes both ENRICHED_SUCCESS and ENRICHED_NO_DATA statuses
 */
export function hasBeenEnriched(contact: IContactIntelligence): boolean {
  return contact.enrichment_status !== ContactEnrichmentStatus.NOT_ENRICHED;
}

/**
 * Check if contact enrichment returned no data
 */
export function hasEnrichmentNoData(contact: IContactIntelligence): boolean {
  return contact.enrichment_status === ContactEnrichmentStatus.ENRICHED_NO_DATA;
}

/**
 * Check if contact has not been enriched yet
 */
export function isNotEnriched(contact: IContactIntelligence): boolean {
  return contact.enrichment_status === ContactEnrichmentStatus.NOT_ENRICHED;
}

/**
 * Get display text for enrichment status
 */
export function getEnrichmentStatusText(status: ContactEnrichmentStatus): string {
  switch (status) {
    case ContactEnrichmentStatus.NOT_ENRICHED:
      return "Not Enriched";
    case ContactEnrichmentStatus.ENRICHED_SUCCESS:
      return "Enriched";
    case ContactEnrichmentStatus.ENRICHED_NO_DATA:
      return "No Data Found";
    default:
      return "Unknown";
  }
}

function normalizeEmailStatusValue(emailStatus: string): string {
  return emailStatus.trim().toLowerCase().replace(/[\s-]+/g, "_");
}

export function normalizeContactEmailStatus(
  emailStatus: string | null | undefined
): string | null {
  if (typeof emailStatus !== "string") {
    return null;
  }

  const normalizedEmailStatus = normalizeEmailStatusValue(emailStatus);
  return normalizedEmailStatus.length > 0 ? normalizedEmailStatus : null;
}

/** Expected bounce rate for catch-all (accept_all) work emails from enrichment verification. */
export const CATCH_ALL_EMAIL_BOUNCE_RATE_PERCENT = 26;

export const CATCH_ALL_EMAIL_CAUTION_LABEL = "Catch-all";

export const CATCH_ALL_EMAIL_CAUTION_TOOLTIP =
  `~${CATCH_ALL_EMAIL_BOUNCE_RATE_PERCENT}% bounce rate. Domain accepts any address, so this email isn't verified.`;

export function isInvalidContactEmailStatus(
  emailStatus: string | null | undefined
): boolean {
  return normalizeContactEmailStatus(emailStatus) === "invalid";
}

export function isCatchAllContactEmailStatus(
  emailStatus: string | null | undefined
): boolean {
  return normalizeContactEmailStatus(emailStatus) === "accept_all";
}

/** @deprecated Use isInvalidContactEmailStatus — only invalid emails are hidden now. */
export function isHiddenContactEmailStatus(
  emailStatus: string | null | undefined
): boolean {
  return isInvalidContactEmailStatus(emailStatus);
}

export function getDisplayableContactEmail(
  contact: Pick<IContactIntelligence, "email" | "email_status">
): string | null {
  if (isInvalidContactEmailStatus(contact.email_status)) {
    return null;
  }

  return contact.email;
}

export function hasDisplayableContactEmail(
  contact: Pick<IContactIntelligence, "email" | "email_status">
): boolean {
  return Boolean(getDisplayableContactEmail(contact));
}

/** Non-empty trimmed strings in an array (skills, summary lines, etc.). */
export function hasNonEmptyTrimmedStringArray(
  arr: string[] | null | undefined
): boolean {
  return (
    Array.isArray(arr) &&
    arr.some((s) => typeof s === "string" && s.trim().length > 0)
  );
}

/** City, state, country joined for display (same ordering as account contacts table). */
export function contactFormattedLocationLine(
  contact: Pick<IContactIntelligence, "city" | "state" | "country">
): string {
  const parts = [contact.city, contact.state, contact.country].filter(
    (p): p is string => typeof p === "string" && p.trim().length > 0
  );
  return parts.join(", ");
}

export function hasContactProfileLocation(
  contact: Pick<IContactIntelligence, "city" | "state" | "country">
): boolean {
  return contactFormattedLocationLine(contact).length > 0;
}

/**
 * Fields that drive the account contacts expanded row (inline panel shows
 * summary and skills only; location/LinkedIn stay in the table columns).
 */
export function hasExpandableProfileDetails(contact: IContactIntelligence): boolean {
  return (
    hasNonEmptyTrimmedStringArray(contact.skills) ||
    hasNonEmptyTrimmedStringArray(contact.summary)
  );
}

/** Row can open expanded intel when enriched (reach + narrative) or profile data exists. */
export function canExpandContactIntelRow(contact: IContactIntelligence): boolean {
  return isSuccessfullyEnriched(contact) || hasExpandableProfileDetails(contact);
}

